import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  AlertColor,
  Box,
  Button,
  CircularProgress,
  Container,
  Snackbar,
  Typography,
} from '@mui/material'
import ContractDetail from '@components/contracts/ContractDetail'
import {
  ClaimStatus,
  Contract,
  ContractStatus,
  LocationType,
  Note,
  PaymentInfo,
  PaymentStatus,
  Session,
  SessionStatus,
} from '@/types/contracts'
import {
  ContractSessionsResponse,
  ContractSessionApiModel,
  getContractSessions,
  // getContractDetail,
  // ContractDetailApiResponse,
  getContractTimeline,
  getContractDocuments,
  downloadContractDocument,
  ContractTimelineItem,
  ContractDocument,
  getContractSessionsDirectly,
} from '@/services/contractsService'
import ContractDetailSeassion from '@/components/contracts/ContractDetailSeassion'

const DEFAULT_INITIAL_EVAL = {
  text: '',
  scores: {},
}

const TherapistSessionDetailPage: React.FC = () => {
  const { contractId ,sessionId} = useParams<{ contractId: string ,sessionId:string}>()
  const navigate = useNavigate()
  const [contract, setContract] = useState<Contract | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeline, setTimeline] = useState<ContractTimelineItem[]>([])
  const [timelineLoading, setTimelineLoading] = useState(false)
  const [contractDocuments, setContractDocuments] = useState<
    ContractDocument[]
  >([])
  const [nextSessionDate, setNextSessionDate] = useState<Date | null>(null)
  const [contractAmount, setContractAmount] = useState<number | null>(null)
  const [sessionLength, setSessionLeangth] = useState<number | null>(null)
  const [sessionData, setSessionData] = useState<any[]>([])
  const [contractDocumentsLoading, setContractDocumentsLoading] =
    useState(false)
  const [documents, setDocuments] = useState<
    Array<{
      id: string
      type: string
      createdOn: string
      status?: 'pending' | 'authorized' | 'rejected'
      downloadUrl?: string
    }>
  >([])
  const [documentsLoading, setDocumentsLoading] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: AlertColor
  }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const loadContract = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)
    try {
      const response = await getContractSessionsDirectly(id)

      setSessionLeangth(response.contract?.service_id?.session_length)
      setNextSessionDate(response.contract?.next_date)
      setContractAmount(response.contract.contract_amount)
      const normalized = buildContractFromSessions(response, id)


      // if (!normalized) {
      //   setError('No session data found for this contract.')
      // }

      // Merge with existing contract to preserve optimistic updates
      setContract(prev => {
        if (!prev || !normalized) return normalized

        // Merge sessions, preserving optimistic updates (sessions with checkInAt)
        const mergedSessions = normalized.sessions.map(serverSession => {
          const existingSession = prev.sessions.find(
            s => s.id === serverSession.id
          )

          // If existing session has optimistic check-in that server doesn't have yet, preserve it
          if (
            existingSession?.evv?.checkInAt &&
            !serverSession.evv?.checkInAt
          ) {
            return existingSession
          }

          // Otherwise use server data
          return serverSession
        })

        return {
          ...normalized,
          sessions: mergedSessions,
        }
      })
    } catch (apiError) {
      console.error(
        '[TherapistContractDetailPage] Failed to load contract',
        apiError
      )
      setError('Unable to load this contract right now. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadTimeline = useCallback(async (id: string) => {
    setTimelineLoading(true)
    try {
      const timelineData = await getContractTimeline(id)
      setTimeline(timelineData)
    } catch (apiError) {
      console.error(
        '[TherapistContractDetailPage] Failed to load timeline',
        apiError
      )
    } finally {
      setTimelineLoading(false)
    }
  }, [])

  const loadContractDocuments = useCallback(async (id: string) => {
    setContractDocumentsLoading(true)
    try {
      const documentsResponse = await getContractDocuments(id)
      setContractDocuments(documentsResponse.data || [])
    } catch (apiError) {
      console.error(
        '[TherapistContractDetailPage] Failed to load contract documents',
        apiError
      )
    } finally {
      setContractDocumentsLoading(false)
    }
  }, [])

  const handleDownloadContractDocument = useCallback(
    async (documentId: string): Promise<string> => {
      try {
        const signedUrl = await downloadContractDocument(documentId)
        if (signedUrl) {
          // Return the URL so the component can handle the download
          return signedUrl
        }
        throw new Error('No download URL received')
      } catch (error) {
        console.error('Failed to download document:', error)
        throw error
      }
    },
    []
  )

  const handleDownloadDocument = useCallback(async (documentId: string) => {
    // TODO: Implement document download when API is available
    console.log('Download document:', documentId)
  }, [])

  const handleUploadDocument = useCallback(() => {
    // TODO: Implement document upload when API is available
    console.log('Upload document')
  }, [])

  const showSnackbar = useCallback(
    (message: string, severity: AlertColor = 'info') => {
      setSnackbar({ open: true, message, severity })
    },
    []
  )

  const handleSnackbarClose = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }, [])

  useEffect(() => {
    if (sessionId) {
      loadContract(sessionId)
      loadTimeline(sessionId)
      loadContractDocuments(sessionId)
      // TODO: Load documents section when API is available
      // loadDocuments(contractId)
    } else {
      setLoading(false)
      setError('Missing contract identifier.')
    }
  }, [contractId, loadContract, loadTimeline, loadContractDocuments])

  const handleUpdateSession = (updatedSession: Session) => {
    if (!contract) return

    setContract(prev =>
      prev
        ? {
          ...prev,
          sessions: prev.sessions.map(session =>
            session.id === updatedSession.id ? updatedSession : session
          ),
        }
        : prev
    )
  }

  const handleUpdateNote = async (updatedNote: Note) => {
    if (!contract) return

    // Helper function to match notes by id or _id
    const noteMatches = (note: any, targetNote: Note): boolean => {
      const noteId = note.id || note._id
      const targetId = targetNote.id || (targetNote as any)._id
      return noteId === targetId
    }

    // Optimistically update the UI
    setContract(prev => {
      if (!prev) return prev

      const updatedSessions = prev.sessions.map(session => {
        // Match by session.id or session._id
        const sessionMatches =
          session.id === updatedNote.sessionId ||
          session._id === updatedNote.sessionId ||
          (session as any)._id === updatedNote.sessionId

        if (!sessionMatches) {
          return session
        }

        // Check if note already exists in the session
        const noteExists = session.notes.some(note =>
          noteMatches(note, updatedNote)
        )

        const notes = noteExists
          ? session.notes.map(note =>
            noteMatches(note, updatedNote) ? updatedNote : note
          )
          : [...session.notes, updatedNote]

        return {
          ...session,
          notes,
        }
      })

      return {
        ...prev,
        sessions: updatedSessions,
      }
    })

    // Refresh contract data from server to get latest notes
    if (contractId) {
      try {
        await loadContract(contractId)
      } catch (error) {
        console.error('Failed to refresh contract after note update:', error)
      }
    }
  }

  const handleBack = () => {
    navigate('/therapy-contracts')
  }

  const showEmptyState = useMemo(
    () => !loading && (!contract || !!error),
    [contract, error, loading]
  )

  const refreshContract = useCallback(async () => {
    if (contractId) {
      await Promise.all([
        loadContract(contractId),
        loadTimeline(contractId),
        loadContractDocuments(contractId),
      ])
    }
  }, [contractId, loadContract, loadTimeline, loadContractDocuments])

  const buildContractFromSessions = (
    response: ContractSessionsResponse,
    fallbackContractId: string
  ): Contract | null => {
    if (!response.sessions || response.sessions.length === 0) {
      const contractDetails = response.contract || null
      const patientInfo = (contractDetails?.patient_id as any) || null
      const patientGender = patientInfo?.gender
      const therapistInfo = (contractDetails?.therapist_id as any) || null
      const patientName = formatFullName(
        patientInfo?.firstName,
        patientInfo?.lastName
      )
      const therapistName = formatFullName(
        therapistInfo?.firstName,
        therapistInfo?.lastName
      )
      const startDate =
        contractDetails?.start_date ||
        contractDetails?.next_date ||
        new Date().toISOString()

      const nextSessionDate =
        contractDetails?.next_date ||
        new Date().toISOString()

      const perSessionAmount =
        contractDetails?.service_id?.private_pay_rate ||
        contractDetails?.total_amount / contractDetails?.total_session ||
        0

      return {
        id: contractDetails._id || '',
        sessionlength: contractDetails?.service_id?.session_length || contractDetails?.session_length || contractDetails?.serviceLengthMinutes || '',
        contract_id: contractDetails.contract_id,
        insuranceName: contractDetails?.contract_id?.insuranceName || contractDetails?.insuranceCardId?.insuranceName || contractDetails?.insuranceName,
        externalId: contractDetails.contract_ids,
        title:
          contractDetails?.service_name ||
          contractDetails?.service_id?.name ||
          contractDetails?.contract_ids ||
          'Therapy Contract',
        patient: {

          id: patientInfo._id || '',
          name: patientName || 'Patient',
          dob: patientInfo.dob || '',
          avatar: patientInfo.profilePhotoUrl || '',
          gender: patientGender || '',
          referringPhysician: patientInfo.referringPhysician || '',
          insuranceInfo: patientInfo.insuranceInfo || '',
        },
        therapist: {
          id: therapistInfo._id || '',
          name: therapistName || 'Provider',
          npi: therapistInfo.providerNpi || therapistInfo.billingNpi,
          licenseNumber: therapistInfo.providerLicenseNumber,
          providerLicenseType: therapistInfo.providerLicenseType,
          providerState: therapistInfo.providerState,
        },
        startDate,
        endDate: contractDetails.end_date,
        plannedSessions: contractDetails.total_session,
        completedSessions: contractDetails.completed_session,
        sessions: [],
        initialEvaluation: {
          ...DEFAULT_INITIAL_EVAL,
        },
        status: (contractDetails.status as ContractStatus) || 'pending',
        diagnosis: undefined,
        treatmentGoals:
          contractDetails.Contracts && contractDetails.Contracts.length > 0
            ? contractDetails.Contracts
            : undefined,
        patientSigned: contractDetails.hasPatientSigned,
        therapistSigned: contractDetails.hasTherapistSigned,
        signedAt: undefined,
        serviceId: contractDetails.service_id?._id || '',
        service_id: contractDetails.service_id,
        agencyId: contractDetails.agencyId || contractDetails.agency_id,
        agency_id: contractDetails.agencyId || contractDetails.agency_id,
        totalAmount: contractDetails.total_amount ?? 0,
        perSessionAmount: perSessionAmount,
        contract_title: contractDetails.contract_title,
        nextSessionDate,
      }
    }



    const [firstSession] = response.sessions
    const contractInfo = (firstSession.contract_id as any) || {}
    const patientInfo = (firstSession.patient_id as any) || {}
    const therapistInfo = (firstSession.therapist_id as any) || {}
    const serviceInfo = (firstSession.service_id as any) || {}
    const perSessionAmount = firstSession.session_amount ?? 0

    const contractId = contractInfo?._id || fallbackContractId
    const patientName = formatFullName(
      patientInfo?.firstName,
      patientInfo?.lastName
    )
    const therapistName = formatFullName(
      therapistInfo?.firstName,
      therapistInfo?.lastName
    )
    const startDate =
      contractInfo?.start_date ||
      contractInfo?.next_date ||
      firstSession.session_date ||
      new Date().toISOString()

    const patientGender = patientInfo?.gender || ''
    const patientReferringPhysician = patientInfo?.referringPhysician || ''
    const patientInsuranceInfo = patientInfo?.insuranceInfo || ''

    const normalizedSessions = response.sessions
      .map(session => normalizeSession(session, contractInfo))
      .sort(
        (a, b) =>
          new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
      )

    return {
      id: contractId,
      sessionlength: serviceInfo?.session_length || contractInfo?.session_length || contractInfo?.serviceLengthMinutes || contractInfo?.duretion || '',
      externalId: contractInfo?.contract_ids,
      insuranceName: contractInfo?.contract_id?.insuranceName || contractInfo?.insuranceCardId?.insuranceName || contractInfo?.insuranceName,
      title: serviceInfo?.name || contractInfo?.contract_ids || 'Therapy Contract',
      patient: {
        id: patientInfo?._id || '',
        name: patientName || 'Patient',
        dob: patientInfo?.dob || '',
        avatar: patientInfo?.profilePhotoUrl || '',
        gender: patientGender,
        referringPhysician: patientReferringPhysician,
        insuranceInfo: patientInsuranceInfo,
      },
      therapist: {
        id: therapistInfo?._id || '',
        name: therapistName || 'Provider',
        npi: therapistInfo?.providerNpi || therapistInfo?.billingNpi,
        licenseNumber: therapistInfo.providerLicenseNumber,
        providerLicenseType: therapistInfo.providerLicenseType,
        providerState: therapistInfo.providerState,
      },
      startDate,
      endDate: contractInfo.end_date,
      plannedSessions: contractInfo.total_session ?? normalizedSessions.length,
      completedSessions: contractInfo.completed_session ?? 0,
      sessions: normalizedSessions,
      initialEvaluation: {
        ...DEFAULT_INITIAL_EVAL,
      },
      status: (contractInfo.status as ContractStatus) || 'pending',
      diagnosis: undefined,
      treatmentGoals:
        contractInfo.Contracts && contractInfo.Contracts.length > 0
          ? contractInfo.Contracts
          : undefined,
      patientSigned: contractInfo.hasPatientSigned,
      therapistSigned: contractInfo.hasTherapistSigned,
      signedAt: undefined,
      envelopeId: (contractInfo as any).envelope_id,
      serviceId: serviceInfo?._id || '',
      service_id: serviceInfo,
      agencyId: contractInfo.agencyId || contractInfo.agency_id,
      agency_id: contractInfo.agencyId || contractInfo.agency_id,
      totalAmount: contractInfo.total_amount ?? 0,
      perSessionAmount: perSessionAmount,
    }
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {loading && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
          gap={2}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading contract...
          </Typography>
        </Box>
      )}

      {showEmptyState && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="40vh"
          gap={2}
        >
          {error ? (
            <Alert severity="error" sx={{ width: '100%', maxWidth: 480 }}>
              {error}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ width: '100%', maxWidth: 480 }}>
              No contract data available to display.
            </Alert>
          )}
          <Button variant="contained" onClick={handleBack}>
            Back to Contracts
          </Button>
        </Box>
      )}

      {!loading && contract && (
        <ContractDetailSeassion
          contract={contract}
          nextSessionDateprop={nextSessionDate}
          sessionData={sessionData}
          sessionLength={sessionLength}
          onBack={handleBack}
          onUpdateSession={handleUpdateSession}
          onUpdateNote={handleUpdateNote}
          onSignContract={refreshContract}
          onRefreshContract={refreshContract}
          onShowSnackbar={showSnackbar}
          timeline={timeline}
          timelineLoading={timelineLoading}
          documents={documents}
          documentsLoading={documentsLoading}
          contractDocuments={contractDocuments}
          contractDocumentsLoading={contractDocumentsLoading}
          onDownloadContractDocument={handleDownloadContractDocument}
          onDownloadDocument={handleDownloadDocument}
          onUploadDocument={handleUploadDocument}
          contractAmount={contractAmount}
        />
      )}

      {/* Global Snackbar for contract-level notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}



const normalizeSession = (
  session: ContractSessionApiModel,
  contractInfo: ContractSessionApiModel['contract_id']
): Session => {
  const scheduledAt = deriveSessionStart(
    session.session_date,
    session.session_slot
  )
  const paymentInfo = buildPaymentInfo(session, contractInfo)
  const locationType = mapLocationType(session.service_id?.modality?.[0])

  return {
    _id: session._id,
    id: session.session_ids || session._id,
    contractId: contractInfo?._id || '',
    contract_id: (typeof session.contract_id === 'string' ? session.contract_id : (session.contract_id as any)?._id) || session.contract_ids || '',
    scheduledAt,
    status: normalizeSessionStatus(session.status),
    evv: session.evvs || {},
    notes: session.notes || [],
    durationMinutes:
      session.duration_mins || session.service_id?.session_length,
    slotDurationMinutes: session.service_id?.session_length,
    claimable: paymentInfo.paymentType === 'insurance',
    locationType,
    paymentInfo,
    session_slot: session.session_slot,
    session_order: session.session_order,
    isPocGenerated: session.isPocGenerated,
    payment_method: session.payment_method,
    invoiceId: session.invoiceId,
    claimId: session.claimId,
    cpt_code: session.cpt_code,
    icd_code: session.icd_code,
    service_id: session.service_id,
    agencyId: (session as any).agencyId || (session as any).agency_id || (contractInfo as any)?.agencyId || (contractInfo as any)?.agency_id,
    agency_id: (session as any).agencyId || (session as any).agency_id || (contractInfo as any)?.agencyId || (contractInfo as any)?.agency_id,
  }
}

const buildPaymentInfo = (
  session: ContractSessionApiModel,
  contractInfo: ContractSessionApiModel['contract_id']
): PaymentInfo => {
  const paymentType =
    contractInfo?.payment_method_type === 'insurance' ? 'insurance' : 'self-pay'
  const totalAmount =
    session.session_amount ??
    contractInfo?.total_amount ??
    session.service_id?.private_pay_rate ??
    0

  const rawPatientShare =
    paymentType === 'insurance'
      ? (session.co_pay ?? 0)
      : totalAmount - (session.approved_amount ?? 0)

  return {
    paymentType,
    totalAmount,
    insurancePaidAmount: session.approved_amount ?? 0,
    coPayAmount: session.co_pay ?? 0,
    patientOwedAmount: Math.max(0, rawPatientShare),
    paymentStatus: normalizePaymentStatus(session.status),
    claimStatus: normalizeClaimStatus(paymentType),
  }
}

const deriveSessionStart = (
  sessionDate?: string,
  sessionSlot?: string
): string => {
  if (!sessionDate) {
    return new Date().toISOString()
  }

  if (!sessionSlot) {
    return new Date(sessionDate).toISOString()
  }

  const [startPart] = sessionSlot.split('-')
  const start = startPart?.trim()

  if (!start) {
    return new Date(sessionDate).toISOString()
  }

  const date = new Date(sessionDate)
  const slotMatch = start.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i)

  if (slotMatch) {
    let hours = parseInt(slotMatch[1], 10)
    const minutes = parseInt(slotMatch[2] ?? '0', 10)
    const meridiem = slotMatch[3]?.toUpperCase()

    if (meridiem === 'PM' && hours < 12) {
      hours += 12
    }
    if (meridiem === 'AM' && hours === 12) {
      hours = 0
    }

    date.setHours(hours, minutes, 0, 0)
  }

  return date.toISOString()
}

const normalizeSessionStatus = (status?: string): SessionStatus => {
  const normalized = (status || '').toLowerCase()

  if (
    normalized === 'scheduled' ||
    normalized === 'rescheduled' ||
    normalized === 'cancelled' ||
    normalized === 'completed' ||
    normalized === 'clocked-out' ||
    normalized === 'missed' ||
    normalized === 'in-progress'
  ) {
    return normalized as SessionStatus
  }

  return 'scheduled'
}

const normalizePaymentStatus = (status?: string): PaymentStatus => {
  const normalized = (status || '').toLowerCase()

  if (normalized === 'completed') {
    return 'fully-paid'
  }

  if (normalized === 'in-progress' || normalized === 'clocked-out') {
    return 'in-progress'
  }

  return 'pending'
}

const normalizeClaimStatus = (
  paymentType: PaymentInfo['paymentType']
): ClaimStatus => {
  if (paymentType === 'insurance') {
    return 'pending'
  }

  return 'not-applicable'
}

const mapLocationType = (modality?: string): LocationType | undefined => {
  if (!modality) return undefined

  const normalized = modality.toLowerCase()

  if (normalized === 'in_person' || normalized === 'in-person') {
    return 'in-person'
  }

  if (normalized === 'home') {
    return 'home'
  }

  if (
    normalized === 'telehealth' ||
    normalized === 'virtual' ||
    normalized === 'online'
  ) {
    return 'telehealth'
  }

  return undefined
}

const formatFullName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(' ').trim()

export default TherapistSessionDetailPage
